import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, ChevronLeft, ChevronRight, Lock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, PageHead, WaButton, inputCls, itDate, saveDoc } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { CalendarSync } from "@/components/CalendarSync";

const SEDI = ["AFRODITE BOUTIQUE HOTEL", "TENUTA LEDO", "CATERING PRESSO SEDE ESTERNA"];
const COLORS = { contratto: "bg-[#0B6E8F] text-white", preventivo: "bg-[#FEF3C7] text-[#92400E]", opzione: "bg-[#FCE7F3] text-[#9D174D]", appuntamento: "bg-[#DCFCE7] text-[#166534]", ricorrenza: "bg-[#EDE9FE] text-[#5B21B6]", annullato: "bg-[#F1F5F9] text-[#64748B] line-through" };
const LEGEND = [["contratto", "Contratto"], ["preventivo", "Preventivo inviato"], ["opzione", "Data in opzione"], ["appuntamento", "Degustazione / sopralluogo"], ["ricorrenza", "Compleanno / anniversario"], ["annullato", "Contratto annullato"]];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function HoldForm({ initial, onDone }) {
  const [f, setF] = useState({ sede: SEDI[0], data: "", cliente_nome: "", cliente_tel: "", giorni: 7, note: "", ...initial });
  const [conf, setConf] = useState([]);
  useEffect(() => {
    if (!f.data) return;
    api.get("/availability", { params: { date: f.data, sede: f.sede } }).then((r) => setConf(r.data.conflicts)).catch(() => {});
  }, [f.data, f.sede]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (await saveDoc("/holds", null, { ...f, giorni: Number(f.giorni) })) onDone();
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Sede"><select className={inputCls} value={f.sede} onChange={set("sede")} data-testid="hold-sede">{SEDI.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Data"><input type="date" className={inputCls} value={f.data} onChange={set("data")} data-testid="hold-date" /></Field>
        <Field label="Cliente"><input className={inputCls} value={f.cliente_nome} onChange={set("cliente_nome")} data-testid="hold-client" /></Field>
        <Field label="Telefono"><input className={inputCls} value={f.cliente_tel} onChange={set("cliente_tel")} data-testid="hold-phone" /></Field>
        <Field label="Giorni di opzione"><input type="number" min={1} max={60} className={inputCls} value={f.giorni} onChange={set("giorni")} data-testid="hold-days" /></Field>
        <Field label="Note"><input className={inputCls} value={f.note} onChange={set("note")} data-testid="hold-note" /></Field>
      </div>
      {conf.length > 0 && <div className="text-xs rounded-md bg-[#FEF2F2] text-[#991B1B] p-3" data-testid="hold-conflicts">Attenzione, data già impegnata:<br />{conf.map((c) => <div key={c.label}>• {c.label}</div>)}</div>}
      <Button className="rounded-full" onClick={save} data-testid="hold-save">Tieni la data in opzione</Button>
    </div>
  );
}

const TIPI = ["Degustazione", "Sopralluogo", "Incontro", "Prova menù", "Altro"];

export function ApptForm({ initial = {}, onDone }) {
  const [f, setF] = useState({ tipo: "Degustazione", data_ora: "", cliente_nome: "", cliente_tel: "", sede: "", note: "", ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (await saveDoc("/appointments", f.id, f)) onDone();
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Tipo"><select className={inputCls} value={f.tipo} onChange={set("tipo")} data-testid="appt-type">{TIPI.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Data e ora"><input type="datetime-local" className={inputCls} value={f.data_ora} onChange={set("data_ora")} data-testid="appt-datetime" /></Field>
        <Field label="Cliente"><input className={inputCls} value={f.cliente_nome} onChange={set("cliente_nome")} data-testid="appt-client" /></Field>
        <Field label="Telefono"><input className={inputCls} value={f.cliente_tel} onChange={set("cliente_tel")} data-testid="appt-phone" /></Field>
        <Field label="Sede"><select className={inputCls} value={f.sede} onChange={set("sede")} data-testid="appt-sede"><option value="">—</option>{SEDI.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Note"><input className={inputCls} value={f.note} onChange={set("note")} data-testid="appt-note" /></Field>
      </div>
      <Button className="rounded-full" onClick={save} disabled={!f.data_ora} data-testid="appt-save">Salva appuntamento</Button>
    </div>
  );
}

function DayDetail({ day, items, reload, close }) {
  const nav = useNavigate();
  const remove = async (it) => {
    try {
      await api.delete(it.type === "opzione" ? `/holds/${it.id}` : `/appointments/${it.id}`);
      reload();
      close();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2" data-testid="day-detail">
      {items.length === 0 && <div className="text-sm text-[#7B929C]">Nessun impegno il {itDate(day)}: la data è libera.</div>}
      {items.map((it) => (
        <div key={it.type + it.id} className="rounded-lg border border-[#D4E6EC] p-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`day-item-${it.id}`}>
          <div><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full mr-2 ${COLORS[it.type]}`}>{it.type}</span><b>{it.title}</b> • {it.detail} {it.time && `• ore ${it.time}`}<div className="text-xs text-[#7B929C]">{it.sede} {it.note && `• ${it.note}`}</div></div>
          <div className="flex gap-2">
            {it.link && <Button size="sm" variant="outline" className="rounded-full" onClick={() => nav(it.link)} data-testid={`day-open-${it.id}`}>Apri</Button>}
            {it.phone && <WaButton phone={it.phone} testId={`day-wa-${it.id}`} text={`🌟 Gentile *${it.title}*,\n\n📅 la contattiamo in merito alla data del *${itDate(it.date)}*${it.time ? ` alle ore ${it.time}` : ""}. ✨\n\nUn cordiale saluto 🌊`} />}
            {["opzione", "appuntamento"].includes(it.type) && <Button size="sm" variant="ghost" className="rounded-full text-[#991B1B]" onClick={() => remove(it)} data-testid={`day-remove-${it.id}`}><Trash2 size={14} /></Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CalendarPage() {
  const [cur, setCur] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [sede, setSede] = useState("");
  const [day, setDay] = useState(null);
  const [modal, setModal] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const k = params.get("new");
    if (k === "appt" || k === "hold") {
      setModal({ kind: k });
      setParams({});
    }
  }, [params, setParams]);
  const start = new Date(cur);
  start.setDate(1 - ((cur.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const reload = () => api.get("/calendar", { params: { start: iso(cells[0]), end: iso(cells[41]) } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur]);
  const byDay = useMemo(() => items.filter((i) => !sede || i.sede === sede || ["appuntamento", "ricorrenza"].includes(i.type)).reduce((m, i) => ({ ...m, [i.date]: [...(m[i.date] || []), i] }), {}), [items, sede]);
  const today = iso(new Date());
  const done = () => { setModal(null); reload(); };
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Sale, date e appuntamenti" title="Calendario">
        <CalendarSync />
        <Button variant="outline" className="rounded-full" onClick={() => setModal({ kind: "hold" })} data-testid="new-hold-button"><Lock size={15} className="mr-2" /> Opzione data</Button>
        <Button className="rounded-full" onClick={() => setModal({ kind: "appt" })} data-testid="new-appt-button"><CalendarPlus size={15} className="mr-2" /> Appuntamento</Button>
      </PageHead>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
          <div className="font-serif-it text-2xl font-semibold capitalize w-48 text-center" data-testid="cal-month">{cur.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</div>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight size={16} /></Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["", ...SEDI].map((s) => <button key={s || "all"} onClick={() => setSede(s)} data-testid={`cal-sede-${s ? SEDI.indexOf(s) : "all"}`} className={`text-xs px-3 py-1.5 rounded-full border ${sede === s ? "bg-[#0D2B3A] text-white border-[#0D2B3A]" : "bg-white border-[#D4E6EC]"}`}>{s ? s.replace("CATERING PRESSO SEDE ESTERNA", "CATERING") : "Tutte le sedi"}</button>)}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">{LEGEND.map(([k, l]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded ${COLORS[k]}`} />{l}</span>)}</div>
      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-hidden" data-testid="calendar-grid">
        <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">{["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d} className="p-2 text-center">{d}</div>)}</div>
        <div className="grid grid-cols-7">
          {cells.map((d) => {
            const k = iso(d);
            const list = byDay[k] || [];
            return (
              <button key={k} onClick={() => setDay(k)} data-testid={`cal-day-${k}`} className={`min-h-[64px] sm:min-h-[104px] p-1.5 text-left border-b border-r border-[#EAF5F7] hover:bg-[#F5FAFB] transition-colors ${d.getMonth() !== cur.getMonth() ? "bg-[#FAFCFD] text-[#A8C6CF]" : ""}`}>
                <div className={`text-xs mb-1 ${k === today ? "inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#34B3B0] text-white" : ""}`}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {list.slice(0, 3).map((it) => <div key={it.type + it.id} className={`truncate text-[10px] sm:text-[11px] px-1.5 py-0.5 rounded ${COLORS[it.type]}`}>{it.time && <span className="hidden sm:inline">{it.time} </span>}{it.title}</div>)}
                  {list.length > 3 && <div className="text-[10px] text-[#7B929C]">+{list.length - 3}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <Modal open={!!day} onOpenChange={(o) => !o && setDay(null)} title={day ? new Date(day + "T12:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""} testId="day-dialog">
        {day && <DayDetail day={day} items={byDay[day] || []} reload={reload} close={() => setDay(null)} />}
        {day && <div className="flex gap-2 pt-2"><Button size="sm" variant="outline" className="rounded-full" onClick={() => { setModal({ kind: "hold", data: day }); setDay(null); }} data-testid="day-add-hold">Metti in opzione</Button><Button size="sm" variant="outline" className="rounded-full" onClick={() => { setModal({ kind: "appt", data: day }); setDay(null); }} data-testid="day-add-appt">Fissa appuntamento</Button></div>}
      </Modal>
      <Modal open={modal?.kind === "hold"} onOpenChange={(o) => !o && setModal(null)} title="Tieni una data in opzione" testId="hold-dialog">
        {modal?.kind === "hold" && <HoldForm initial={modal.data ? { data: modal.data, sede: sede || SEDI[0] } : {}} onDone={done} />}
      </Modal>
      <Modal open={modal?.kind === "appt"} onOpenChange={(o) => !o && setModal(null)} title="Nuovo appuntamento" testId="appt-dialog">
        {modal?.kind === "appt" && <ApptForm initial={modal.data ? { data_ora: `${modal.data}T17:00` } : {}} onDone={done} />}
      </Modal>
    </div>
  );
}
